import { API_BASE_URL } from '../config/backendConfig';
import { YOVO_ASSIST_WELCOME } from '../config/yovoAssistEmptyState';

const CHATBOT_API = `${API_BASE_URL}/chatbot`;
const SESSION_STORAGE_KEY = 'yovo_chatbot_session_id';

/**
 * Get or create session ID
 * Persists in localStorage, cleared when chat is cleared
 */
function getSessionId() {
  try {
    return localStorage.getItem(SESSION_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Store session ID from backend response
 */
function setSessionId(sessionId) {
  if (sessionId) {
    try {
      localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    } catch { /* ignore storage errors */ }
  }
}

/**
 * Clear session ID (call when chat is cleared)
 */
export function clearChatbotSession() {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch { /* ignore storage errors */ }
}

/**
 * Get auth headers if user is logged in
 */
function getAuthHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  try {
    // Must match AuthContext / api.js — login stores yovo_auth_token (not "token")
    const token = localStorage.getItem('yovo_auth_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch { /* ignore storage errors */ }
  return headers;
}

export const chatbotService = {
  sendMessage: async (message, conversationHistory = [], offTopicCount = 0) => {
    try {
      const sessionId = getSessionId();
      const response = await fetch(`${CHATBOT_API}/message`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ message, conversationHistory, offTopicCount, sessionId }),
      });

      if (response.status === 429) {
        return { success: false, message: 'Rate limit exceeded. Please try again in a moment.', error: 'RATE_LIMIT' };
      }

      const data = await response.json();
      
      // Persist sessionId from response
      if (data.sessionId) {
        setSessionId(data.sessionId);
      }
      
      return data;
    } catch (error) {
      return {
        success: false,
        message: 'Sorry, I\'m having trouble connecting right now. Please try again later.',
        error: 'NETWORK_ERROR'
      };
    }
  },

  sendMessageStream: async (message, conversationHistory = [], onChunk, offTopicCount = 0) => {
    const EMPTY_REPLY =
      "Sorry — I didn't get a complete reply just now. Please try sending that again.";
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutMs = 90000;
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;

    const finalizeOk = (payload) => {
      const text = String(payload?.message || '').trim();
      const hasActions = Array.isArray(payload?.actions) && payload.actions.length > 0;
      if (!text && !hasActions) {
        return {
          success: false,
          message: EMPTY_REPLY,
          error: 'EMPTY_RESPONSE',
          actions: [],
          suggestedActions: null,
          tripBrief: null,
          refinements: null,
          source: null,
          offTopicCount: payload?.offTopicCount ?? 0,
          intentCategory: payload?.intentCategory || null,
          sessionId: payload?.sessionId || null,
          usage: null,
        };
      }
      if (!text && hasActions) {
        return {
          ...payload,
          success: true,
          message: 'Here’s what I found — tap below to continue.',
        };
      }
      return { ...payload, success: true, message: text || payload.message };
    };

    try {
      const sessionId = getSessionId();
      const response = await fetch(`${CHATBOT_API}/message/stream`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ message, conversationHistory, offTopicCount, sessionId }),
        signal: controller?.signal,
      });

      if (!response.ok) {
        if (response.status === 429) {
          return { success: false, message: 'Rate limit exceeded. Please try again in a moment.', error: 'RATE_LIMIT' };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullMessage = '';
      let lastMeta = null;
      const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);

              if (data === '[DONE]') {
                return finalizeOk({
                  ...(lastMeta || {}),
                  message: fullMessage,
                  usage: null,
                });
              }

              try {
                const parsed = JSON.parse(data);

                // Persist sessionId from response
                if (parsed.sessionId) {
                  setSessionId(parsed.sessionId);
                }

                if (parsed.error) {
                  return {
                    success: false,
                    message: EMPTY_REPLY,
                    error: parsed.error,
                    sessionId: parsed.sessionId || null,
                  };
                }
                // Prefer full content snapshots from server (buffered stream)
                if (typeof parsed.content === 'string') {
                  fullMessage = parsed.content;
                  if (parsed.content && typeof onChunk === 'function') {
                    onChunk(parsed.content);
                  }
                }
                // Final payload usually includes actions / metadata
                if (
                  parsed.actions
                  || parsed.tripBrief
                  || parsed.refinements
                  || parsed.suggestedActions
                  || parsed.done
                  || typeof parsed.content === 'string'
                ) {
                  // Merge metadata across SSE events. A trailing `{ done: true }`
                  // event must not erase actions sent with the content event.
                  const nextMeta = { ...(lastMeta || {}) };
                  if (hasOwn(parsed, 'actions')) nextMeta.actions = parsed.actions || [];
                  if (hasOwn(parsed, 'suggestedActions')) nextMeta.suggestedActions = parsed.suggestedActions || null;
                  if (hasOwn(parsed, 'tripBrief')) nextMeta.tripBrief = parsed.tripBrief || null;
                  if (hasOwn(parsed, 'refinements')) nextMeta.refinements = parsed.refinements || null;
                  if (hasOwn(parsed, 'source')) nextMeta.source = parsed.source || null;
                  if (hasOwn(parsed, 'offTopicCount')) nextMeta.offTopicCount = parsed.offTopicCount ?? 0;
                  if (hasOwn(parsed, 'intentCategory')) nextMeta.intentCategory = parsed.intentCategory || null;
                  if (hasOwn(parsed, 'sessionId')) nextMeta.sessionId = parsed.sessionId || null;
                  lastMeta = nextMeta;
                  // If this event already has content + meta, return after [DONE];
                  // keep reading so we don't miss [DONE] / trailing events.
                }
              } catch (_) { /* skip invalid JSON lines */ }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      return finalizeOk({
        ...(lastMeta || {}),
        message: fullMessage,
        usage: null,
      });
    } catch (error) {
      const aborted = error?.name === 'AbortError';
      return {
        success: false,
        message: aborted
          ? 'That took too long — please try again in a moment.'
          : 'Sorry, I\'m having trouble connecting right now. Please try again later.',
        error: aborted ? 'TIMEOUT' : 'NETWORK_ERROR'
      };
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  },

  getTravelRecommendations: async (destination, preferences = {}) => {
    const message = `I'm planning a trip to ${destination}. Can you give me some travel recommendations? My preferences: ${JSON.stringify(preferences)}`;
    return chatbotService.sendMessage(message);
  },

  getFlightSearchHelp: async (query) => {
    const message = `I need help with flight search: ${query}`;
    return chatbotService.sendMessage(message);
  },

  getItinerarySuggestions: async (destination, duration, interests = []) => {
    const message = `I'm planning a ${duration} day trip to ${destination}. My interests are: ${interests.join(', ')}. Can you suggest an itinerary?`;
    return chatbotService.sendMessage(message);
  },

  getTravelTips: async (destination, travelType = 'general') => {
    const message = `What are some useful travel tips for ${destination}? I'm interested in ${travelType} travel.`;
    return chatbotService.sendMessage(message);
  },

  analyzeFlightOptions: async (flights) => {
    const message = `I have these flight options: ${JSON.stringify(flights)}. Can you help me analyze which one is the best choice and why?`;
    return chatbotService.sendMessage(message);
  },

  getWeatherAdvice: async (destination, travelDate) => {
    const message = `I'm traveling to ${destination} on ${travelDate}. What should I know about the weather and what should I pack?`;
    return chatbotService.sendMessage(message);
  },

  getBudgetTips: async (destination, budget) => {
    const message = `I'm planning a trip to ${destination} with a budget of ${budget}. What are some budget-friendly tips and suggestions?`;
    return chatbotService.sendMessage(message);
  }
};

export const fallbackResponses = {
  greeting: YOVO_ASSIST_WELCOME,
  flightSearch: "I can help you plan your trip and find flights! Tell me where you'd like to go, and I'll create a detailed itinerary for you.",
  noApiKey: "Yovo is temporarily unavailable. Please try again shortly.",
  rateLimit: "I'm getting a lot of travel requests right now! Please try again in a moment, or tell me about your dream destination.",
  error: "I'm having trouble connecting right now, but I can still help you plan your trip! Tell me where you'd like to travel.",
  empty: "Sorry — I didn't get a complete reply just now. Please try sending that again.",
  travelTips: "Here are some travel planning tips: Research your destination's culture and customs, check visa requirements, book flights in advance for better prices, and always have travel insurance!",
  itinerary: "I love planning itineraries! Tell me your destination and how many days you have, and I'll create a detailed day-by-day plan with the best attractions, local experiences, and cultural insights."
};

export default chatbotService;
